import React from 'react'
import {
  stepAgentConfig,
  stepSelectContainerMethod,
  stepInteractContrastUI,
  stepAgentConfigPipeline,
  stepSelectOperatingSystemNormalize
} from './common'
import {
  stepSelectKubernetesOperator,
  stepKubernetesLabelDeployments as stepKubernetesLabelDeploymentsNode,
  stepKubernetesDownloadSetup,
  stepKubernetesInstallDeployHelm
} from './common-kubernetes'
import {
  stepContainerInstallOperator,
  stepContainerConfigureOperator,
  stepContainerInjectWorkloads as stepContainerInjectWorkloadsNode,
  stepContainerClusterAgentAndAgent
} from './common-container'
import { formatStyles } from '../../../../react-intl-helpers'
import { ImageIcon } from '../common/image-icon'
import { logo_docker, logo_manual, logo_pipeline } from '../images'
import {
  IconBitbucket,
  IconGithub
} from '@contrast-security-inc/design-system-foundations'

const stepKubernetesLabelDeployments =
  stepKubernetesLabelDeploymentsNode('NODE')

const stepContainerInjectWorkloads = stepContainerInjectWorkloadsNode('NODE')

const commonFinalSteps = [stepInteractContrastUI]

const stepInstallAgentBasic = {
  defaultIsOpen: true,
  titleMsg: 'title_installAgent',
  Content: {
    message: 'detail_installAgentNodeBasic',
    values: {
      ...formatStyles.link({
        href: 'https://www.npmjs.com/package/@contrast/agent',
        isExternal: true
      })
    }
  }
}

const selectMethodNodeNormalizeAgentDownload = {
  titleMsg: 'title_applicationDeploymentMethod',
  defaultIsOpen: true,
  Content: {
    options: [
      {
        value: 'basic',
        Content: 'Install manually',
        icon: <ImageIcon src={logo_manual} alt="manually" />
      },
      {
        value: 'container',
        Content: 'Install with a container',
        icon: <ImageIcon src={logo_docker} alt="container" />
      },
      {
        value: 'pipeline',
        Content: {
          message: 'title_installPipeline'
        },
        icon: <ImageIcon src={logo_pipeline} alt="container" />
      }
    ]
  }
}

const stepsDockerContainerNormalizeAgentDownload = [
  {
    defaultIsOpen: true,
    titleMsg: 'title_buildImageWithAgent',
    Content: {
      message: 'detail_buildImageWithAgentNode'
    }
  },
  { ...stepAgentConfig },
  ...commonFinalSteps
]

// LINUX STEPS
const stepsLinuxBasicNormalizeAgentDownload = [
  stepInstallAgentBasic,
  {
    titleMsg: 'title_addCommandsToYourAppFile',
    Content: {
      message: 'detail_addCommandsNodePackageJson',
      values: {
        code: `"scripts": {
  "contrast": "node --import @contrast/agent <app>.js",
  "start": ...,
  "test": ...
}`
      }
    }
  },
  stepAgentConfig,
  {
    titleMsg: 'title_runApplication',
    Content: { message: 'detail_runApplicationNodeBasic' }
  },
  ...commonFinalSteps
]

const stepsWindowsBasicNormalizeAgentDownload =
  stepsLinuxBasicNormalizeAgentDownload

const stepsLinuxContainerNormalizeAgentDownload = [
  {
    ...stepSelectContainerMethod,
    branchKey: 'node-container-method',
    branches: {
      docker: stepsDockerContainerNormalizeAgentDownload,
      kubernetes: [
        {
          ...stepSelectKubernetesOperator,
          branchKey: 'node-kubernetes-op',
          branches: {
            helm: [
              stepKubernetesDownloadSetup,
              stepKubernetesLabelDeployments,
              stepKubernetesInstallDeployHelm,
              ...commonFinalSteps
            ],
            manifest: [
              stepContainerInstallOperator,
              stepContainerConfigureOperator,
              stepContainerInjectWorkloads,
              stepContainerClusterAgentAndAgent,
              ...commonFinalSteps
            ]
          }
        }
      ]
    }
  }
]

const stepSelectPipelineMethod = {
  defaultIsOpen: true,
  titleMsg: 'title_selectDeployMethodPipeline',
  Content: {
    options: [
      {
        value: 'github',
        Content: 'Github',
        icon: <IconGithub color="black" />
      },
      {
        value: 'bitbucket',
        Content: 'Bitbucket',
        icon: <IconBitbucket color="blue50" />
      }
    ]
  }
}

const githubInstallAgent = {
  defaultIsOpen: true,
  titleMsg: 'title_installAgent',
  Content: {
    message: 'detail_installGithubNode',
    values: {
      code1: `- name: Install Agent 
  shell: bash 
  run: npm install @contrast/agent
`,
      code2: `
env: 
   CONTRAST__API__TOKEN: \${{ secrets.ContrastAPIToken }} 
   CONTRAST__APPLICATION__NAME: \${{ secrets.ContrastApplicationName }} 
   CONTRAST__APPLICATION__METADATA: \${{ secrets.ContrastApplicationMetadata }} 
   ... 
   steps: 
     - name: Run with Contrast
       shell: bash 
       run: node --import @contrast/agent [application entrypoint]
`,
      token: '{{ secrets.ContrastAPIToken }}',
      name: '{{ secrets.ContrastApplicationName }}',
      metadata: '{{ secrets.ContrastApplicationMetadata }}',
      options: '"-javaagent:/contrast.jar"'
    }
  }
}

const bitbucketInstallAgent = {
  defaultIsOpen: true,
  titleMsg: 'title_installAgent',
  Content: {
    message: 'detail_installBitbucketNode',
    values: {
      code: `- step:
  script:
    - npm install @contrast/agent
`
    }
  }
}

const pipelineSteps = [
  {
    ...stepSelectPipelineMethod,
    branchKey: 'linux-java-pipeline-method',
    branches: {
      github: [
        githubInstallAgent,
        stepAgentConfigPipeline,
        ...commonFinalSteps
      ],
      bitbucket: [
        bitbucketInstallAgent,
        stepAgentConfigPipeline,
        ...commonFinalSteps
      ]
    }
  }
]

// main steps tree
export const nodeWizardStepsNormalizeAgentDownload = [
  {
    ...stepSelectOperatingSystemNormalize,
    branches: {
      linux: [
        {
          ...selectMethodNodeNormalizeAgentDownload,
          branchKey: 'node-method-linux',
          branches: {
            basic: stepsLinuxBasicNormalizeAgentDownload,
            container: stepsLinuxContainerNormalizeAgentDownload,
            pipeline: pipelineSteps
          }
        }
      ],
      windows: [
        {
          ...selectMethodNodeNormalizeAgentDownload,
          branchKey: 'node-method-windows',
          branches: {
            basic: stepsWindowsBasicNormalizeAgentDownload,
            container: stepsDockerContainerNormalizeAgentDownload,
            pipeline: pipelineSteps
          }
        }
      ],
      macos: [
        {
          ...selectMethodNodeNormalizeAgentDownload,
          branchKey: 'node-method-macos',
          branches: {
            basic: stepsLinuxBasicNormalizeAgentDownload,
            container: stepsLinuxContainerNormalizeAgentDownload,
            pipeline: pipelineSteps
          }
        }
      ]
    }
  }
]
